import { get, map, size } from "lodash"
import createModule from "../createModule"
import view from "./view"
import {
  generateClassificationQuery,
  classificationsQuery,
  generateClassificationQueryForContributors,
  getClassifications,
} from "helpers/classification"
import { getTableColumns, getSelectedColumns, getFilteredColumnsSelector, getContentSets, getContentSetsLoaded } from "selectors/securities-table"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "model/compositeSecurity"
import { getFacetedFilterId, getFacetedFilter, getWatchlistFacetSecuritiesCount } from "./selectors/facets"
import {
  getWatchlist,
  getPage,
  getSelectedSecurities,
  getCurrentPageSelectionStatus,
  getToolbarActive,
  getFiltersVisible,
  getFilterRaw,
  getWatchlistsMenuVisible,
  getColumnSelectorOpen,
  getColumnSearches,
  getWatchlistTotalSecurities,
  getClassificationsTiers,
  getSort,
  getModelId,
  getCurrency,
} from "./selectors"
import { getMissingDataFiles, betasSubQuery } from "selectors/missingDataFiles"
import { getIsOpPro } from "modules/account-root/selectors"
import { getHistoricalFactorSensitivityIsAvailable } from "modules/securities-finder/selectors"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import withOutletContext from "hocs/withOutletContext"
import { MODEL_METADATA_QUERY } from "resources/models"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"

export default withOutletContext(createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    watchlistId: props.watchlist.id,
    selectedDate: props.selectedDate,
    modelId: getModelId(state, props),
    sort: getSort(state, props),
    page: state.modules.watchlist.page,
    columnSearches: getColumnSearches(state, props),
    selectedColumns: get(state, "modules.watchlist.selectedColumns"),
    filter: getFacetedFilter(state, props),
    filterId: getFacetedFilterId(state, props),
    contentSets: getContentSets(state),
    contentSetsLoaded: getContentSetsLoaded(state),
    missingDataFiles: getMissingDataFiles(state),
    classificationsTiers: getClassificationsTiers(state, props),
    classifications: getClassifications(state),
    isOpPro: getIsOpPro(state),
    isHistoricalFactorSensitivityAvailable: getHistoricalFactorSensitivityIsAvailable(state),
  }),
  required: ["account"],
  data: [{
    resource: "modelCountries",
    as: null,
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/countries`,
  }, {
    resource: "modelCurrencies",
    as: null,
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/currencies`,
  }, {
    resource: "watchlistSecurities",
    passthrough: true,
    url: ({ watchlistId, isOpPro, selectedDate, modelId, page, sort, filterId, filter, contentSets, contentSetsLoaded, missingDataFiles, classifications, isHistoricalFactorSensitivityAvailable }) => {
      if (!contentSetsLoaded) {
        return null
      }

      return {
        id: `${watchlistId}-${sort.id}-${sort.type}-${sort.direction}-${page}-${selectedDate}-${filterId}-${modelId}`,
        query: `query($modelId: String!, $filter: [SecuritySearchFilter!]!, $on: Date!, $skip: Int!, $sort: [SecuritySearchSort]) {
        model(id: $modelId) {
          securitySearch (filter: $filter, on: $on, skip: $skip, sort: $sort) {
            count
            securities {
              id
              assetClass
              ticker
              mic
              description
              internalId
              country
              currency
              ${generateClassificationQueryForContributors("GICS", classifications)}
              averageDailyVolume
              marketCapitalization
              factorExposure {
                id
                zScore
              }
              ${contentSets.length > 0 ? map(contentSets, ({ id, escapedId }) => `${escapedId}: factorExposure (contentSetId: "${id}") { id, zScore } `) : ""}
              ${!isHistoricalFactorSensitivityAvailable ? "" : `historicalFactorSensitivity: factorSensitivity(type: ${FACTOR_SENSITIVITY_TYPES.HISTORICAL}) {
                factors {
                  id
                  sensitivity
                }
              }`}
              ${isOpPro ? "" : `risk {
                standardDeviation {
                  total
                }
                varianceDecomposition {
                  summary {
                    factors
                    specific
                  }
                }
              }
              predictedFactorSensitivity: factorSensitivity(type: ${FACTOR_SENSITIVITY_TYPES.PREDICTED}) {
                factors {
                  id
                  sensitivity
                }
              }`}
              ${betasSubQuery(missingDataFiles)}
            }
          }
        }
      }`,
        variables: {
          on: selectedDate,
          modelId,
          skip: page * 200,
          sort: sort.getter(sort.id, sort.direction),
          filter,
        },
      }
    },
  }, {
    resource: "watchlistPreferences",
    url: ({ accountId, modelId }) => {
      if (!modelId || !accountId) {
        return null
      }

      return ({
        id: `watchlistPreferences-${accountId}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "watchlistSelectedColumns",
              key: `${modelId}`,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    ...CONTENT_SETS_METADATA_QUERY,
    as: null,
  }, {
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "classificationsValues",
    as: null,
    url: ({ classificationsTiers, selectedDate }) => {
      if (!classificationsTiers || size(classificationsTiers) === 0 || !selectedDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      const innerQuery = generateClassificationQuery(classificationsTiers)
      return {
        id: `classificationsValues-${selectedDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
            ${innerQuery}
          }`,
      }
    },
  }, {
    resource: "historicalFactorSensitivityAvailability",
    url: ({ account, modelId, selectedDate }) => ({
      url: `/api/${account}/m/${modelId}/factorReturns/hasEnoughHistory`,
      id: `historicalFactorSensitivityAvailability-${selectedDate}-${modelId}-${account}`,
      payload: { selectedDate },
    }),
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    watchlist: getWatchlist(state, props),
    watchlistTotalSecurities: getWatchlistTotalSecurities(state, props),
    watchlistFacetsTotalSecurities: getWatchlistFacetSecuritiesCount(state, props),
    columns: getTableColumns(state, props),
    page: getPage(state, props),
    status: get(state, "global.data.watchlistSecurities.status"),
    selectedSecurities: getSelectedSecurities(state, props),
    currentPageSelectionStatus: getCurrentPageSelectionStatus(state, props),
    toolbarActive: getToolbarActive(state, props),
    filtersVisible: getFiltersVisible(state, props),
    watchlistsMenuVisible: getWatchlistsMenuVisible(state, props),
    selectedColumns: getSelectedColumns(state, props),
    filteredColumnsSelector: getFilteredColumnsSelector(state, props),
    columnSelectorOpen: getColumnSelectorOpen(state, props),
    filterSubset: getFilterRaw(state, props),
    accountCurrency: getCurrency(state, props),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile2,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
}))
